import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Check, X, AlertCircle } from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { useGroupedExtractionFields, useUpdateExtractionField, useValidateExtractionField } from '@/hooks/api/use-document-training';
import type { FieldGroup, GroupedFieldChild, FieldValidationStatus } from '@/types/api/document-training';
import { useToast } from '@/hooks/use-toast';

interface GroupedFieldsViewProps {
    trainingDocId: string;
    parsingId: string;
}

interface CollapsibleGroupProps {
    group: FieldGroup;
    onUpdateField: (fieldValueId: string, newValue: unknown) => Promise<void>;
    onValidateField: (fieldValueId: string, isCorrect: boolean, notes?: string) => Promise<void>;
    updatingFieldId: string | null;
    validatingFieldId: string | null;
}

function getMatchStatusBadge(status: string | null | undefined) {
    switch (status) {
        case 'exact_match':
            return <Badge color="success" size="sm">Exact</Badge>;
        case 'alias_match':
            return <Badge color="warning" size="sm">Alias</Badge>;
        case 'unmatched':
            return <Badge color="error" size="sm">Unmatched</Badge>;
        default:
            return <Badge color="gray" size="sm">-</Badge>;
    }
}

function getValidationStatusBadge(status: FieldValidationStatus) {
    switch (status) {
        case 'confirmed':
            return <Badge color="success" size="sm">Confirmed</Badge>;
        case 'corrected':
            return <Badge color="blue" size="sm">Corrected</Badge>;
        case 'rejected':
            return <Badge color="error" size="sm">Rejected</Badge>;
        default:
            return <Badge color="gray" size="sm">Pending</Badge>;
    }
}

function FieldRow({
    field,
    onUpdate,
    onValidate,
    isUpdating,
    isValidating,
    showGroupLabel = false,
    groupLabel,
}: {
    field: GroupedFieldChild;
    onUpdate: (fieldValueId: string, newValue: unknown) => Promise<void>;
    onValidate: (fieldValueId: string, isCorrect: boolean, notes?: string) => Promise<void>;
    isUpdating: boolean;
    isValidating: boolean;
    showGroupLabel?: boolean;
    groupLabel?: string;
}) {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(field.effective_value || '');

    // Notes modal state for rejection
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
    const [rejectionNotes, setRejectionNotes] = useState('');

    const handleSave = async () => {
        if (editValue === field.effective_value) {
            setIsEditing(false);
            return;
        }
        try {
            await onUpdate(field.field_value_id, editValue);
            setIsEditing(false);
        } catch {
            // Error handled by parent
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            setEditValue(field.effective_value || '');
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        // Save on blur (clicking outside)
        handleSave();
    };

    // Handle reject click - open notes modal
    const handleRejectClick = () => {
        setIsNotesModalOpen(true);
    };

    // Submit rejection with notes
    const handleSubmitRejection = async () => {
        await onValidate(field.field_value_id, false, rejectionNotes || undefined);
        setIsNotesModalOpen(false);
        setRejectionNotes('');
    };

    // Cancel rejection modal
    const handleCancelRejection = () => {
        setIsNotesModalOpen(false);
        setRejectionNotes('');
    };

    return (
        <>
        <tr className="border-b border-secondary last:border-b-0 hover:bg-secondary/30 transition-colors">
            <td className="py-2 pr-3 text-sm">
                <div className="flex flex-col">
                    <span className="font-medium text-primary">
                        {showGroupLabel && groupLabel ? `${groupLabel}` : field.field_key}
                    </span>
                    {field.attribute_label && (
                        <span className="text-xs text-tertiary">{field.attribute_label}</span>
                    )}
                </div>
            </td>
            <td className="py-2 pr-3 text-sm">
                {isEditing ? (
                    <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleBlur}
                        disabled={isUpdating}
                        className="w-full px-2 py-1 text-sm border border-brand rounded bg-primary text-primary focus:outline-none focus:ring-1 focus:ring-brand"
                        autoFocus
                        placeholder="Press Enter to save, Esc to cancel"
                    />
                ) : (
                    <div
                        className="cursor-pointer hover:bg-secondary/50 px-2 py-1 rounded transition-colors"
                        onClick={() => setIsEditing(true)}
                    >
                        <span className="text-primary">{field.effective_value || '-'}</span>
                    </div>
                )}
            </td>
            <td className="py-2 pr-3 text-sm text-center">
                {field.confidence_score !== null && field.confidence_score !== undefined ? (
                    <span className={field.confidence_score >= 0.8 ? 'text-fg-success-primary' : field.confidence_score >= 0.5 ? 'text-fg-warning-primary' : 'text-fg-error-primary'}>
                        {(field.confidence_score * 100).toFixed(0)}%
                    </span>
                ) : (
                    <span className="text-tertiary">-</span>
                )}
            </td>
            <td className="py-2 pr-3 text-sm">
                {getMatchStatusBadge(field.match_status)}
            </td>
            <td className="py-2 pr-3 text-sm">
                {getValidationStatusBadge(field.validation_status)}
            </td>
            <td className="py-2 text-sm">
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onValidate(field.field_value_id, true)}
                        disabled={isValidating}
                        className={`p-1.5 rounded transition-colors disabled:cursor-not-allowed ${
                            field.validation_status === 'confirmed'
                                ? 'bg-success-solid text-white'
                                : 'bg-secondary hover:bg-success-secondary text-fg-tertiary hover:text-fg-success-primary'
                        }`}
                        title="Confirm correct"
                    >
                        <Check className="size-4" />
                    </button>
                    <button
                        onClick={handleRejectClick}
                        disabled={isValidating}
                        className={`p-1.5 rounded transition-colors disabled:cursor-not-allowed ${
                            field.validation_status === 'rejected'
                                ? 'bg-error-solid text-white'
                                : 'bg-secondary hover:bg-error-secondary text-fg-tertiary hover:text-fg-error-primary'
                        }`}
                        title="Mark incorrect"
                    >
                        <X className="size-4" />
                    </button>
                </div>
            </td>
        </tr>

        {/* Notes Modal for Rejection - rendered via Portal */}
            {isNotesModalOpen && createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={handleCancelRejection}
                    />
                    <div className="relative z-10 w-full max-w-md rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                        <h3 className="text-lg font-semibold text-primary mb-2">
                            {t('training.detail.fields.rejectModal.title', { defaultValue: 'Mark as Incorrect' })}
                        </h3>
                        <p className="text-sm text-tertiary mb-4">
                            {t('training.detail.fields.rejectModal.description', {
                                defaultValue: 'Why is this field value incorrect? (optional)'
                            })}
                        </p>
                        <textarea
                            value={rejectionNotes}
                            onChange={(e) => setRejectionNotes(e.target.value)}
                            placeholder={t('training.detail.fields.rejectModal.placeholder', {
                                defaultValue: 'Enter reason...'
                            })}
                            rows={3}
                            className="w-full px-3 py-2 text-sm text-primary bg-secondary border border-secondary rounded-lg focus:outline-none focus:ring-2 focus:ring-brand placeholder:text-tertiary mb-4"
                        />
                        <div className="flex items-center justify-end gap-3">
                            <Button
                                onClick={handleCancelRejection}
                                size="sm"
                                color="secondary"
                            >
                                {t('common.cancel', { defaultValue: 'Cancel' })}
                            </Button>
                            <Button
                                onClick={handleSubmitRejection}
                                size="sm"
                                color="primary-destructive"
                                iconLeading={X}
                                isDisabled={isValidating}
                            >
                                {isValidating
                                    ? t('loading')
                                    : t('training.detail.fields.rejectModal.confirm', { defaultValue: 'Mark Incorrect' })}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
}

function CollapsibleGroup({
    group,
    onUpdateField,
    onValidateField,
    updatingFieldId,
    validatingFieldId,
}: Omit<CollapsibleGroupProps, 'trainingDocId' | 'parsingId'>) {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const [isExpanded, setIsExpanded] = useState(false);

    const hasIssues = group.unmatched_count > 0 || group.pending_count > 0;

    return (
        <div className="border border-secondary rounded-lg overflow-hidden">
            {/* Collapsible Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-3 bg-secondary/30 hover:bg-secondary/50 transition-colors"
            >
                <div className="flex items-center gap-3">
                    {isExpanded ? (
                        <ChevronDown className="size-5 text-tertiary" />
                    ) : (
                        <ChevronRight className="size-5 text-tertiary" />
                    )}
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-primary">{group.display_label}</span>
                        {group.is_array_item && (
                            <Badge color="blue" size="sm">Item {(group.array_index ?? 0) + 1}</Badge>
                        )}
                        {hasIssues && (
                            <AlertCircle className="size-4 text-warning" />
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {/* Stats badges */}
                    <div className="flex items-center gap-2 text-xs">
                        <span className="text-tertiary">{group.child_count} fields</span>
                        {group.validated_count > 0 && (
                            <Badge color="success" size="sm">{group.validated_count} validated</Badge>
                        )}
                        {group.pending_count > 0 && (
                            <Badge color="gray" size="sm">{group.pending_count} pending</Badge>
                        )}
                    </div>
                </div>
            </button>

            {/* Collapsed Preview - JSON */}
            {!isExpanded && group.collapsed_preview_json && (
                <div className="px-4 py-2 bg-primary border-t border-secondary">
                    <pre className="text-xs text-tertiary font-mono overflow-x-auto whitespace-pre-wrap max-h-24 overflow-y-auto">
                        {group.collapsed_preview_json}
                    </pre>
                </div>
            )}

            {/* Expanded - Individual Fields */}
            {isExpanded && (
                <div className="p-4 bg-primary">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-secondary">
                                <th className="py-2 pr-3 text-left text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.field', { defaultValue: 'Field' })}
                                </th>
                                <th className="py-2 pr-3 text-left text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.value', { defaultValue: 'Value' })}
                                </th>
                                <th className="py-2 pr-3 text-center text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.confidence', { defaultValue: 'Conf.' })}
                                </th>
                                <th className="py-2 pr-3 text-left text-xs font-medium text-tertiary uppercase">
                                    Match
                                </th>
                                <th className="py-2 pr-3 text-left text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.validation', { defaultValue: 'Status' })}
                                </th>
                                <th className="py-2 text-left text-xs font-medium text-tertiary uppercase">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.children.map((field) => (
                                <FieldRow
                                    key={field.field_value_id}
                                    field={field}
                                    onUpdate={onUpdateField}
                                    onValidate={onValidateField}
                                    isUpdating={updatingFieldId === field.field_value_id}
                                    isValidating={validatingFieldId === field.field_value_id}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export function GroupedFieldsView({ trainingDocId, parsingId }: GroupedFieldsViewProps) {
    const { t } = useTranslation(['pages:documents-admin', 'common']);
    const toast = useToast();
    const [updatingFieldId, setUpdatingFieldId] = useState<string | null>(null);
    const [validatingFieldId, setValidatingFieldId] = useState<string | null>(null);

    // Fetch grouped fields
    const { data, isLoading, isError, refetch } = useGroupedExtractionFields(trainingDocId, parsingId);

    // Mutation hooks
    const updateField = useUpdateExtractionField();
    const validateField = useValidateExtractionField();

    // Handle field update
    const handleUpdateField = async (fieldValueId: string, newValue: unknown) => {
        setUpdatingFieldId(fieldValueId);
        try {
            await updateField.mutateAsync({
                trainingDocId,
                parsingId,
                fieldValueId,
                data: { value: String(newValue) },
            });
            toast.success(t('training.detail.fields.updateSuccess', { defaultValue: 'Field updated successfully' }));
            refetch();
        } catch (error) {
            console.error('Error updating field:', error);
            toast.error(t('training.detail.fields.updateError', { defaultValue: 'Error updating field' }));
            throw error;
        } finally {
            setUpdatingFieldId(null);
        }
    };

    // Handle field validation - now accepts optional notes parameter
    const handleValidateField = async (fieldValueId: string, isCorrect: boolean, notes?: string) => {
        setValidatingFieldId(fieldValueId);
        try {
            await validateField.mutateAsync({
                trainingDocId,
                parsingId,
                fieldValueId,
                data: {
                    status: isCorrect ? 'confirmed' : 'rejected',
                    notes: notes || null,
                },
            });
            toast.success(
                isCorrect
                    ? t('training.detail.fields.markedCorrect', { defaultValue: 'Field marked as correct' })
                    : t('training.detail.fields.markedIncorrect', { defaultValue: 'Field marked as incorrect' })
            );
            refetch();
        } catch (error) {
            console.error('Error validating field:', error);
            toast.error(t('training.detail.fields.validateError', { defaultValue: 'Error validating field' }));
            throw error;
        } finally {
            setValidatingFieldId(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand"></div>
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="text-center py-8">
                <p className="text-error">
                    {t('training.detail.fields.loadError', { defaultValue: 'Error loading fields' })}
                </p>
            </div>
        );
    }

    if (data.groups.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-tertiary">
                    {t('training.detail.fields.noFields', { defaultValue: 'No extracted fields' })}
                </p>
            </div>
        );
    }

    // Sort groups: single fields first, then multi-field groups by size
    const sortedGroups = [...data.groups].sort((a, b) => a.child_count - b.child_count);

    // Separate single-field groups from multi-field groups
    const singleFieldGroups = sortedGroups.filter(g => g.child_count === 1);
    const multiFieldGroups = sortedGroups.filter(g => g.child_count > 1);

    return (
        <div className="space-y-4">
            {/* Summary Stats */}
            <div className="flex items-center gap-4 text-sm">
                <span className="text-tertiary">
                    {data.total_fields} fields
                    {multiFieldGroups.length > 0 && ` (${multiFieldGroups.length} groups)`}
                </span>
                <div className="flex items-center gap-2">
                    {data.validated_fields > 0 && (
                        <Badge color="success" size="sm">{data.validated_fields} validated</Badge>
                    )}
                    {data.pending_fields > 0 && (
                        <Badge color="gray" size="sm">{data.pending_fields} pending</Badge>
                    )}
                    {data.exact_matches > 0 && (
                        <Badge color="success" size="sm">{data.exact_matches} exact</Badge>
                    )}
                    {data.alias_matches > 0 && (
                        <Badge color="warning" size="sm">{data.alias_matches} alias</Badge>
                    )}
                    {data.unmatched > 0 && (
                        <Badge color="error" size="sm">{data.unmatched} unmatched</Badge>
                    )}
                </div>
            </div>

            {/* Single Fields - Displayed directly in a table (no collapsible) */}
            {singleFieldGroups.length > 0 && (
                <div className="border border-secondary rounded-lg overflow-hidden">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-secondary bg-secondary/30">
                                <th className="py-2 px-3 text-left text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.field', { defaultValue: 'Field' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.value', { defaultValue: 'Value' })}
                                </th>
                                <th className="py-2 px-3 text-center text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.confidence', { defaultValue: 'Conf.' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-medium text-tertiary uppercase">
                                    Match
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-medium text-tertiary uppercase">
                                    {t('training.detail.fields.headers.validation', { defaultValue: 'Status' })}
                                </th>
                                <th className="py-2 px-3 text-left text-xs font-medium text-tertiary uppercase">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {singleFieldGroups.map((group) => {
                                const field = group.children[0];
                                return (
                                    <FieldRow
                                        key={field.field_value_id}
                                        field={field}
                                        onUpdate={handleUpdateField}
                                        onValidate={handleValidateField}
                                        isUpdating={updatingFieldId === field.field_value_id}
                                        isValidating={validatingFieldId === field.field_value_id}
                                        showGroupLabel
                                        groupLabel={group.display_label}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Multi-Field Groups - Collapsible */}
            {multiFieldGroups.length > 0 && (
                <div className="space-y-3">
                    {multiFieldGroups.map((group) => (
                        <CollapsibleGroup
                            key={group.group_key}
                            group={group}
                            onUpdateField={handleUpdateField}
                            onValidateField={handleValidateField}
                            updatingFieldId={updatingFieldId}
                            validatingFieldId={validatingFieldId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
